// Garde-fou des aliments explicitement exclus (allergies, aliments évités,
// autres restrictions). Partagé par le Dossier application et l'éditeur
// « Semaine, menus & recettes ». Comparaison littérale, jamais d'inférence.

function normalizeFoodText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    // 2026-08-21 — la ligature était remplacée AVANT le passage en minuscules,
    // donc seul le « œ » minuscule était traité. Un ingrédient écrit « Œufs »
    // perdait sa première lettre (« ufs ») et ne correspondait plus jamais à
    // l'exclusion « oeuf ». L'œuf est un allergène majeur.
    .toLocaleLowerCase("fr")
    .replace(/œ/g, "oe")
    .replace(/æ/g, "ae")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

// Anissa écrit ses exclusions comme elle les dit : « sans arachides »,
// « pas de lactose ». Cet habillage n'est pas un nom d'aliment et empêchait
// la comparaison d'aboutir dès que l'ingrédient était composé (« Beurre de
// cacahuète » face à « sans cacahuète » ne déclenchait rien). On ne garde
// que l'aliment.
const MOTS_DE_REGLE = "sans|pas de|pas d|pas|ni|aucun|aucune|zero|no|non|neant|rien|eviter|evite|exclure|exclu|exclus";
const HABILLAGE_DE_REGLE = new RegExp(`^(?:${MOTS_DE_REGLE})\\s+`);
const REGLE_SEULE = new RegExp(`^(?:${MOTS_DE_REGLE})$`);
// « Pas d'allergie », « pas d'intolérance » disent qu'il n'y a RIEN à exclure.
// Une fois l'habillage retiré il reste un mot qui parle de la catégorie, pas
// d'un aliment : le garder inverserait le sens de la phrase.
const MOT_DE_CATEGORIE = /^(?:allergies?|intolerances?|intolerences?|restrictions?|regimes?|aliments?|contre indications?)$/;

function retirerHabillageDeRegle(value) {
  let resultat = value;
  // Deux passes : « sans aucun produit laitier » existe aussi.
  for (let i = 0; i < 2; i += 1) resultat = resultat.replace(HABILLAGE_DE_REGLE, "").trim();
  return resultat;
}

/**
 * Liste des aliments explicitement exclus, tels qu'Anissa les a écrits, ramenés
 * à des noms comparables. Exporté pour être testable : c'est la moitié amont du
 * garde-fou, et ses ratés ne se voient pas autrement.
 */
export function explicitFoodExclusions(snapshot) {
  return analyzeFoodExclusions(snapshot).exclusions;
}

// Tournures d'introduction courantes, retirées pour ne garder que l'aliment
// (« allergie aux arachides » → « arachides »). Aucun synonyme : seul le nom
// écrit par Anissa est comparé.
const INTRODUCTIONS = [
  /^allergi(?:e|es|que|ques)\s+(?:a\s+la|a\s+l|aux|au|a|des|du|de\s+la|de\s+l|de|d)\s+/,
  /^intoleran(?:ce|ces|t|te|ts|tes)\s+(?:a\s+la|a\s+l|aux|au|a|des|du|de\s+la|de\s+l|de|d)\s+/,
  /^evit(?:e|er|es)\s+(?:les|le|la|l|des|du|de\s+la|de\s+l|de|d)\s+/,
];

// « évite les fritures » : l'article restant n'appartient pas à l'aliment.
// « allergique au kiwi et à la noisette » : la suite de la liste garde sa
// préposition (« à la noisette »).
const ARTICLE_INITIAL = /^(?:a\s+la|a\s+l|aux|au|a|les|le|la|l|des|du|de\s+la|de\s+l)\s+/;

// Ce qui ne se réduit pas à un nom d'aliment court n'est jamais deviné.
const MOTS_DE_PROSE = /(?:^|\s)(?:je|j|me|m|moi|mon|ma|mes|severe|severes|selon|parfois|souvent|peu|trop)(?:\s|$)/;
const MAX_MOTS_ALIMENT = 4;

/**
 * Lecture complète des champs d'exclusion :
 * - `exclusions` : noms d'aliments courts, comparés littéralement ;
 * - `uninterpretable` : fragments écrits en prose que le code ne sait pas
 *   réduire à un aliment. Ils ne sont pas devinés : Anissa vérifie elle-même.
 */
export function analyzeFoodExclusions(snapshot) {
  const values = [snapshot?.allergies, snapshot?.alimentsEvites, snapshot?.restrictionsAutre];
  const exclusions = [];
  const uninterpretable = [];
  const fragments = values
    .flatMap((value) => String(value || "").split(/[,;\n/]+/))
    .flatMap((fragment) => fragment.split(/\s+(?:et|ou)\s+/i));
  for (const fragment of fragments) {
    const original = fragment.replace(/\s+/g, " ").trim();
    if (!original) continue;
    // Les remarques entre parenthèses (« (sévère) ») ne sont pas des aliments.
    let value = normalizeFoodText(original.replace(/\([^)]*\)/g, " "));
    value = retirerHabillageDeRegle(value);
    for (const introduction of INTRODUCTIONS) value = value.replace(introduction, "").trim();
    value = retirerHabillageDeRegle(value).replace(ARTICLE_INITIAL, "").trim();
    // Ce qui ne désigne aucun aliment ne doit pas devenir une exclusion : une
    // entrée vide, un mot de règle seul, ou un mot qui nomme la catégorie.
    if (!value || REGLE_SEULE.test(value) || MOT_DE_CATEGORIE.test(value)) continue;
    if (value.split(" ").length > MAX_MOTS_ALIMENT || /\d/.test(value) || MOTS_DE_PROSE.test(value)) {
      if (!uninterpretable.includes(original)) uninterpretable.push(original);
      continue;
    }
    if (!exclusions.includes(value)) exclusions.push(value);
  }
  return { exclusions, uninterpretable };
}

/** Message affiché pour un fragment que le code refuse d'interpréter. */
export function uninterpretableExclusionMessage(fragment) {
  return `Exclusion non interprétable : « ${fragment} » — vérifie toi-même ce repas`;
}

/**
 * Un ingrédient proposé désigne-t-il l'aliment explicitement exclu ?
 *
 * Exporté pour être testable : ce qu'il attrape, et surtout ce qu'il n'attrape
 * pas, décide de ce qui arrive dans l'assiette d'une cliente. Les limites
 * réelles sont documentées dans `sameExplicitFood.test.js`.
 *
 * Comparaison littérale de noms d'aliments, volontairement. Décider que
 * « sans lactose » interdit le lait serait une inférence clinique, et celles-ci
 * n'appartiennent qu'à Anissa.
 */
export function sameExplicitFood(label, exclusion) {
  const normalizedLabel = normalizeFoodText(label);
  if (!normalizedLabel || !exclusion) return false;
  const singular = (value) => value.split(" ").map((word) => (
    word.length > 3 && /[sx]$/.test(word) ? word.slice(0, -1) : word
  )).join(" ");
  const left = singular(normalizedLabel);
  const right = singular(exclusion);
  return left === right || left.includes(` ${right} `) || ` ${left} `.includes(` ${right} `)
    || ` ${right} `.includes(` ${left} `);
}

const MENU_FIELDS = [
  ["title", "le titre", (choice) => [choice?.title]],
  ["hint", "la description", (choice) => [choice?.hint]],
  ["ingredients", "les ingrédients", (choice) => [
    ...(Array.isArray(choice?.recipe?.ingredients) ? choice.recipe.ingredients : []),
    ...(Array.isArray(choice?.recipe?.ingredients_structured)
      ? choice.recipe.ingredients_structured.map((item) => item?.label)
      : []),
  ]],
  ["preparation", "la préparation", (choice) => (Array.isArray(choice?.recipe?.preparation) ? choice.recipe.preparation : [])],
  ["tip", "la consigne", (choice) => [choice?.recipe?.tip]],
];

/**
 * Cherche, dans tout le texte d'un menu écrit à la main (titre, description,
 * ingrédients, étapes, consigne), un aliment explicitement exclu.
 *
 * @param {Array<{ label: string, title: string, hint?: string, recipe?: object }>} choices
 * @param {{ allergies?: string, alimentsEvites?: string, restrictionsAutre?: string }} snapshot
 * @returns {Array<{ message: string, field: string, text: string, exclusion: string }>}
 */
export const FOOD_CONFLICT_ACTIONS = Object.freeze({
  // Lot importé : rien à confirmer dans le dossier, il faut reformuler.
  REFORMULATE: "reformule ou décide avec Anissa",
  // Éditeur Semaine : une confirmation tracée par conflit est possible.
  CONFIRM: "confirme si c’est compatible ou reformule",
});

export function findMenuFoodConflicts(choices, snapshot, { action = FOOD_CONFLICT_ACTIONS.REFORMULATE } = {}) {
  const exclusions = explicitFoodExclusions(snapshot);
  if (exclusions.length === 0) return [];
  const conflicts = [];
  for (const choice of choices || []) {
    for (const [field, fieldLabel, read] of MENU_FIELDS) {
      for (const text of menuTextLines(read(choice))) {
        // Une ligne peut contenir PLUSIEURS aliments exclus : un conflit par
        // couple (ligne, exclusion), chacun avec sa propre confirmation.
        for (const exclusion of exclusions.filter((candidate) => sameExplicitFood(text, candidate))) {
          conflicts.push({
            choiceId: choice.id ?? null,
            label: choice.label,
            field,
            fieldLabel,
            text,
            exclusion,
            // Correspondance littérale : elle peut bloquer à tort (« Pain sans
            // gluten » pour l'exclusion « gluten »). Le message dit quoi faire.
            message: `${choice.label} : ${fieldLabel} mentionne un aliment explicitement exclu (${exclusion}) — bloqué par précaution — ${action}.`,
          });
        }
      }
    }
  }
  return conflicts;
}

/**
 * Forme canonique d'un texte contrôlé : une ligne non vide, sans espaces aux
 * bords. L'éditeur, l'application et la publication découpent et hachent
 * exactement de la même façon.
 */
export function menuTextLines(values) {
  return (Array.isArray(values) ? values : [values])
    .filter((value) => typeof value === "string")
    .flatMap((value) => value.split(/\r?\n/))
    .map((line) => line.trim())
    .filter(Boolean);
}

/** Toutes les exclusions présentes dans une ligne (jamais seulement la première). */
export function matchingExclusions(text, exclusions) {
  return (exclusions || []).filter((exclusion) => sameExplicitFood(text, exclusion));
}
